import { useMemo } from 'react'
import { isMacOs } from 'react-device-detect'

import SendOutlinedIcon from '@ant-design/icons/SendOutlined'

import TimelineDot from '@mui/lab/TimelineDot'
import OutlinedInput from '@mui/material/OutlinedInput'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@components/_mui/IconButton'

import useDraft from '@shared/hooks/src/useDraft'
import useUserSetting from '@shared/hooks/src/useUserSetting'
import { useMe } from '@shared/providers/src/MeProvider'
import { UserSettingName } from '@shared/utils'

import Avatar from '@components/Avatar'

const styles = {
  input: {
    flex: '1 1 auto',
  },
  dot: {
    p: 0,
    borderWidth: 1,
  },
  sendBtn: {
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    '&:hover': {
      bgcolor: 'primary.dark',
      color: 'primary.contrastText',
    },
  },
}

export default function TimelineNoteCreation({ autoFocus = false, draftKey, disabled = false, onSend }) {
  const me = useMe()
  const sendOnEnter = useUserSetting(me, UserSettingName.SendOnEnter)?.value ?? true
  const { draft, setDraft, removeDraft } = useDraft(draftKey)

  const handleSend = () => {
    if (!draft) return
    onSend(draft)
    removeDraft()
  }

  const handleKeyDown = (e) => {
    if (sendOnEnter && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const placeholder = useMemo(() => {
    const enterText = isMacOs ? 'Return' : 'Enter'

    return sendOnEnter
      ? `Press '${enterText}' to leave a note, 'Shift + ${enterText}' to start a new line`
      : `Press '${enterText}' to start a new line`
  }, [sendOnEnter])

  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flex: '0 0 auto', pr: 2 }}>
      <TimelineDot variant="outlined" sx={styles.dot}>
        <Avatar user={me} variant="circular" />
      </TimelineDot>
      <OutlinedInput
        multiline
        size="small"
        maxRows={6}
        autoFocus={autoFocus}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        sx={styles.input}
        value={draft}
      />
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Tooltip title="Send">
          <span>
            <IconButton color="primary" disabled={!draft || disabled} onClick={handleSend} sx={styles.sendBtn}>
              <SendOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  )
}
